import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { touch, change, getFormSyncErrors, FieldArray } from 'redux-form';
import PropTypes from 'prop-types';
import axios from 'axios';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { useParams, useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { Grid, Field, Flex } from 'nallian-shared-ui/lib/components';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { isValidEmail, generateErrorsForRequiredFields } from 'nallian-shared-ui/lib/lib/validate';
import {
  InputFieldValueComponent,
  SelectFieldValueComponent,
} from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { showToaster } from 'nallian-shared-ui/lib/lib/showToaster';
import { countryOptions } from '../constants/countries';
import apiRoutes from '../../../../app/api/routes';
import PrincipalDetailsTab from './tabs/PrincipalDetailsTab';
import communityRolesService from '../services/communityRolesService';
import communityOnboardingTemplatesService from '../services/communityOnboardingTemplatesService';
import communityOnboardingTemplateService from '../services/communityOnboardingTemplateService';
import { TemplateFlex } from '../styles';
import TemplateInfo from './TemplateInfo';
import { Hidden, Step, StepNumber } from '../../../app/styles';
import FormHeader from '../../../app/components/FormHeader';
import { debounceAsyncValidate } from '../../../../app/helpers/debouncedAsyncValidate';
import IdentificationCodeFields from './fields/IdentificationCodeFields';
import { validateIdentificationCodes } from '../helpers/validateIdentificationCodes';
import typesService from '../../../../mgmt/identitficationCodeTypes/services/typesService';
import communityIdentificationCodeTypesService from '../services/communityIdentificationCodeTypesService';

const FORM_NAME = 'FormOrganisationNew';

export const DEFAULT_TEMPLATE_ID = '_';

const FIRST_TAB_FIELDS = ['onboardingTemplateId'];
const SECOND_TAB_FIELDS = ['organisation.name', 'organisation.city', 'organisation.country'];
const THIRD_TAB_FIELDS = [
  'principal.fullName',
  'principal.primaryEmailAddress',
  'principal.languageId',
  'principal.timeZone',
];

const IDENTIFICATION_CODE_FIELDS = ['identificationCodes'];

const ASYNC_FIELDS = ['organisation.name'];
const REQUIRED_FIELDS = [...FIRST_TAB_FIELDS, ...SECOND_TAB_FIELDS, ...THIRD_TAB_FIELDS];

const STEPS = {
  TEMPLATE: 'TEMPLATE',
  ORGANISATION: 'ORGANISATION',
  USER: 'USER',
};

const checkNestedKeys = (obj, keys) => keys.some(key => get(obj, key));

const validate = (values, { t, identificationCodeTypes = [] }) => {
  let errors = {};

  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  if (
    !values.principal ||
    (values.principal && !values.principal.primaryEmailAddress) ||
    values.principal.primaryEmailAddress.length < 0 ||
    !isValidEmail(values.principal.primaryEmailAddress)
  ) {
    errors.principal = {
      ...(errors.principal || {}),
      primaryEmailAddress: t('primaryEmailAddressEmptyErr'),
    };
  }

  validateIdentificationCodes(values?.identificationCodes, errors, identificationCodeTypes, t);

  return errors;
};

const asyncValidate = (values, _, props) => {
  if (
    props.initialValues &&
    get(values, 'organisation.name') === get(props.initialValues, 'organisation.name')
  ) {
    return new Promise(resolve => resolve());
  }

  const url = apiRoutes.getOrganisationsByName(get(values, 'organisation.name'));

  return new Promise((resolve, reject) => {
    axios
      .head(url)
      .then(res => {
        if (res?.status === 200) {
          const errors = {
            organisation: { name: 'That organisation name is taken' },
          };
          showToaster('error', errors.organisation.name);
          reject(errors);
        } else resolve();
      })
      .catch(err => {
        if (err?.response?.status === 404) resolve();
        else {
          const errors = {
            organisation: { name: 'That organisation name is taken' },
          };
          showToaster('error', errors.organisation.name);
          reject(errors);
        }
      });
  });
};

// Debounce your asyncValidate function
const debouncedAsyncValidate = debounceAsyncValidate(asyncValidate, 2000);

const OrganisationForm = ({
  onSubmit,
  dataLoading,
  submitProcessing,
  data,
  header,
  communityId,
  dispatchCommunityRolesGet,
  communityRolesFetch,
  dispatchCommunityOnboardingTemplatesGet,
  communityOnboardingTemplatesFetch,
  dispatchCommunityOnboardingTemplateGet,
  communityOnboardingTemplateFetch,
  returnUrl,
  communityIdentificationCodeTypesFetch,
  dispatchCommunityIdentificationCodeTypesGet,
  identificationCodeTypesFetch,
  dispatchIdentificationCodeTypesGet,
  isCommunityView,
}) => {
  const { t } = useTranslation();
  const { action } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const [step, setStep] = useState(STEPS.TEMPLATE);

  const handleSetStep = useCallback((_, item) => setStep(item.step), [setStep]);

  const formValues = useSelector(state => get(state, `form.${FORM_NAME}.values`));
  const asyncValidating = Boolean(
    useSelector(state => get(state, `form.${FORM_NAME}.asyncValidating`))
  );

  useEffect(dispatchIdentificationCodeTypesGet, [dispatchIdentificationCodeTypesGet]);

  useEffect(() => {
    if (communityId) dispatchCommunityIdentificationCodeTypesGet(communityId);
  }, [communityId, dispatchCommunityIdentificationCodeTypesGet]);

  const identificationCodeTypes = useMemo(() => {
    if (isCommunityView) {
      return (communityIdentificationCodeTypesFetch.value || []).map(idcodeType => ({
        key: idcodeType.type?.tag,
        value: idcodeType.type?.tag,
        text: idcodeType.type?.name,
        validationPattern: idcodeType.validationPattern,
        isRequired: idcodeType.isRequired,
      }));
    }
    return (identificationCodeTypesFetch.value || []).map(idcodeType => ({
      key: idcodeType.tag,
      value: idcodeType.tag,
      text: idcodeType.name,
      validationPattern: idcodeType.validationPattern,
    }));
  }, [
    isCommunityView,
    communityIdentificationCodeTypesFetch.value,
    identificationCodeTypesFetch.value,
  ]);

  const handlePreviousStep = useCallback(() => {
    const keys = Object.keys(STEPS);
    const nextIndex = keys.indexOf(step) - 1;
    setStep(keys[nextIndex]);
  }, [step, setStep]);

  const handleNextStep = useCallback(() => {
    const validationErrors = validate(formValues, { t, identificationCodeTypes });

    if (
      step === STEPS.ORGANISATION &&
      checkNestedKeys(validationErrors, [...SECOND_TAB_FIELDS, ...IDENTIFICATION_CODE_FIELDS])
    ) {
      SECOND_TAB_FIELDS.forEach(field => dispatch(touch(FORM_NAME, field)));
      (formValues.identificationCodes || []).forEach((_, i) => {
        dispatch(touch(FORM_NAME, `identificationCodes[${i}].value`));
        dispatch(touch(FORM_NAME, `identificationCodes[${i}].identificationCodeTypeTag`));
      });
      return;
    }

    const keys = Object.keys(STEPS);
    const nextIndex = keys.indexOf(step) + 1;
    setStep(keys[nextIndex]);
  }, [step, formValues, identificationCodeTypes, dispatch, t, setStep]);

  // const handleGoBack = useCallback(() => history.push(returnRoute), [returnRoute]);

  const templateId = useSelector(state =>
    get(state, `form.${FORM_NAME}.values.onboardingTemplateId`)
  );

  const templateOptions = useMemo(
    () => [
      {
        key: DEFAULT_TEMPLATE_ID,
        value: DEFAULT_TEMPLATE_ID,
        text: t('useNoTemplate'),
      },
      ...(communityOnboardingTemplatesFetch.value || []).map(template => ({
        key: template.id,
        value: template.id,
        text: template.name,
      })),
    ],
    [t, communityOnboardingTemplatesFetch.value]
  );

  useEffect(() => {
    if (templateId && templateId !== DEFAULT_TEMPLATE_ID)
      dispatchCommunityOnboardingTemplateGet(templateId);
  }, [dispatchCommunityOnboardingTemplateGet, templateId]);

  useEffect(() => dispatchCommunityRolesGet(communityId, t), [
    dispatchCommunityRolesGet,
    communityId,
    t,
  ]);

  useEffect(() => dispatchCommunityOnboardingTemplatesGet(communityId), [
    dispatchCommunityOnboardingTemplatesGet,
    communityId,
  ]);

  useEffect(() => {
    dispatch(
      change(
        FORM_NAME,
        'organisationRoles',
        (communityOnboardingTemplateFetch.value?.communityRoleSuggestions || []).map(
          crs => crs.communityRole?.tag
        )
      )
    );
  }, [action, communityOnboardingTemplateFetch.value, dispatch]);

  useEffect(() => {
    if (templateId === DEFAULT_TEMPLATE_ID || !templateId) {
      dispatch(change(FORM_NAME, 'organisationRoles', null));
    }
  }, [templateId, dispatch]);

  const roleOptions = useMemo(
    () =>
      get(communityRolesFetch.value, 'communityRoles', []).map(r => ({
        key: r.tag,
        value: r.tag,
        text: r.alias || r.name,
      })),
    [communityRolesFetch.value]
  );

  const handleGoBack = useCallback(() => navigate(returnUrl), [returnUrl, navigate]);

  const errors = useSelector(state => getFormSyncErrors(FORM_NAME)(state));
  const submitFailed = useSelector(state => get(state, `form.${FORM_NAME}.submitFailed`));

  const firstTabsErrors = useMemo(
    () => submitFailed && Object.keys(pick(errors, FIRST_TAB_FIELDS)).length > 0,
    [errors, submitFailed]
  );

  const secondTabsErrors = useMemo(
    () => submitFailed && Object.keys(pick(errors, SECOND_TAB_FIELDS)).length > 0,
    [errors, submitFailed]
  );

  const thirdTabsErrors = useMemo(
    () => submitFailed && Object.keys(pick(errors, THIRD_TAB_FIELDS)).length > 0,
    [errors, submitFailed]
  );

  const isLastStep = step === STEPS.USER;
  const isFirstStep = step === STEPS.TEMPLATE;

  return (
    <FormModal
      header={header}
      customOnSubmit={!isLastStep ? handleNextStep : undefined}
      onSubmit={onSubmit}
      dataLoading={dataLoading}
      submitProcessing={
        step === STEPS.USER ? submitProcessing || asyncValidating : submitProcessing
      }
      validate={validate}
      asyncValidate={debouncedAsyncValidate}
      asyncBlurFields={ASYNC_FIELDS}
      form={FORM_NAME}
      data={data}
      closeIcon
      onDimmerClick={handleGoBack}
      onCancel={!isFirstStep ? handlePreviousStep : handleGoBack}
      cancelButtonContent={isFirstStep ? t('cancel') : t('back')}
      saveButtonIcon={isLastStep ? 'save' : 'arrow right'}
      saveButtonContent={isLastStep ? t('sendInvitation') : t('next')}
      saveButtonIconPosition={isLastStep ? 'left' : 'right'}
      saveDisabled={asyncValidating}
      backButtonProps={!isFirstStep ? undefined : { hideIcon: true, animated: false }}
      identificationCodeTypes={identificationCodeTypes}
    >
      <Step.Group fluid>
        <Step
          fluid
          link
          width="33.33%"
          active={step === STEPS.TEMPLATE}
          step={STEPS.TEMPLATE}
          onClick={handleSetStep}
          error={firstTabsErrors}
        >
          <StepNumber>1</StepNumber>
          <Step.Content>
            <Step.Title>{t('template')}</Step.Title>
            <Step.Description>{t('templateInfo')}</Step.Description>
          </Step.Content>
        </Step>
        <Step
          fluid
          link
          width="33.33%"
          active={step === STEPS.ORGANISATION}
          step={STEPS.ORGANISATION}
          onClick={handleSetStep}
          error={secondTabsErrors}
        >
          <StepNumber>2</StepNumber>
          <Step.Content>
            <Step.Title>{t('organisation')}</Step.Title>
            <Step.Description>{t('organisationInfo')}</Step.Description>
          </Step.Content>
        </Step>
        <Step
          fluid
          link
          width="33.33%"
          active={step === STEPS.USER}
          step={STEPS.USER}
          onClick={handleSetStep}
          error={thirdTabsErrors}
        >
          <StepNumber>3</StepNumber>
          <Step.Content>
            <Step.Title>{t('user')}</Step.Title>
            <Step.Description>{t('userInfo')}</Step.Description>
          </Step.Content>
        </Step>
      </Step.Group>
      <Hidden hidden={step !== STEPS.TEMPLATE}>
        <FormHeader content={t('template')} />
        <TemplateFlex fluid direction="column" margin="0 0 1rem" visible>
          <Flex fluid margin="0 0 1rem 0">
            <p>
              <i>
                {t('templateInfo1')}
                <br />
                {t('templateInfo2')}
              </i>
            </p>
          </Flex>
          <Grid>
            <Grid.Column mobile={8} tablet={8} computer={8}>
              <Field
                fluid
                search
                label={t('template')}
                name="onboardingTemplateId"
                options={templateOptions}
                valueComponent={SelectFieldValueComponent}
                disabled={submitProcessing || dataLoading}
              />
            </Grid.Column>
            {templateId &&
              communityOnboardingTemplateFetch.value &&
              templateId !== DEFAULT_TEMPLATE_ID && (
                <Grid.Column mobile={16} tablet={16} computer={16}>
                  <TemplateInfo template={communityOnboardingTemplateFetch.value} />
                </Grid.Column>
              )}
          </Grid>
        </TemplateFlex>
      </Hidden>
      <Hidden hidden={step !== STEPS.ORGANISATION}>
        <FormHeader content={t('organisation')} />
        <Grid margin="1rem -1rem 0 -1rem">
          <Grid.Column mobile={8} tablet={8} computer={8}>
            <Field
              autoFocus
              loading={asyncValidating}
              label={t('organisationName')}
              type="text"
              name="organisation.name"
              valueComponent={InputFieldValueComponent}
              disabled={submitProcessing || dataLoading}
            />
          </Grid.Column>
          <Grid.Column mobile={8} tablet={8} computer={8}>
            <Field
              name="organisationRoles"
              label={t('roles')}
              valueComponent={SelectFieldValueComponent}
              options={roleOptions}
              disabled={submitProcessing || dataLoading}
              multiple
              search
              fluid
            />
          </Grid.Column>
          <Grid.Column mobile={8} tablet={8} computer={8}>
            <Field
              label={t('city')}
              type="text"
              name="organisation.city"
              valueComponent={InputFieldValueComponent}
              disabled={submitProcessing || dataLoading}
            />
          </Grid.Column>
          <Grid.Column mobile={8} tablet={8} computer={8}>
            <Field
              search
              clearable
              label={t('country')}
              name="organisation.country"
              type="select"
              valueComponent={SelectFieldValueComponent}
              options={countryOptions}
              disabled={submitProcessing || dataLoading}
            />
          </Grid.Column>
          <Grid.Column mobile={16} tablet={16} computer={16}>
            <FieldArray
              name="identificationCodes"
              component={IdentificationCodeFields}
              submitProcessing={submitProcessing}
              identificationCodeTypes={identificationCodeTypes}
            />
          </Grid.Column>
        </Grid>
      </Hidden>
      <Hidden hidden={step !== STEPS.USER}>
        <FormHeader content={t('user')} />
        <PrincipalDetailsTab />
      </Hidden>
    </FormModal>
  );
};

OrganisationForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  communityId: PropTypes.string.isRequired,
  header: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatchCommunityRolesGet: PropTypes.func.isRequired,
  communityRolesFetch: PropTypes.object.isRequired,
  dispatchCommunityOnboardingTemplatesGet: PropTypes.func.isRequired,
  communityOnboardingTemplatesFetch: PropTypes.object.isRequired,
  dispatchCommunityOnboardingTemplateGet: PropTypes.func.isRequired,
  communityOnboardingTemplateFetch: PropTypes.object.isRequired,
  dispatchCommunityIdentificationCodeTypesGet: PropTypes.func.isRequired,
  communityIdentificationCodeTypesFetch: PropTypes.object.isRequired,
  dispatchIdentificationCodeTypesGet: PropTypes.func.isRequired,
  identificationCodeTypesFetch: PropTypes.object.isRequired,
  returnUrl: PropTypes.string.isRequired,
  isCommunityView: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  dataLoading: PropTypes.bool,
  data: PropTypes.object,
};

OrganisationForm.defaultProps = {
  dataLoading: false,
  submitProcessing: false,
  data: undefined,
  isCommunityView: false,
};

export default compose(
  communityOnboardingTemplatesService,
  communityOnboardingTemplateService,
  communityRolesService,
  communityIdentificationCodeTypesService,
  typesService
)(OrganisationForm);
